import React from "react";
import { ChevronDown, Search } from "lucide-react";
import * as Popover from "@radix-ui/react-popover";
import { Command } from "cmdk";

export default function QuotationItemSelect({
  value,
  onChange,
  options = [],
  placeholder,
  testid,
  open,
  onOpenChange,
  disabled = false,
}) {
  const selected = options.find((option) => option.value === value);

  const select = (nextValue) => {
    onChange(nextValue);
    onOpenChange(false);
  };

  return (
    <Popover.Root open={open} onOpenChange={(nextOpen) => { if (!disabled) onOpenChange(nextOpen); }}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={`flex w-full items-center justify-between gap-2 rounded-lg border border-zinc-300 bg-card px-3 py-2.5 text-left text-sm transition-colors hover:border-primary/50 ${
            open ? "border-primary ring-2 ring-primary/20" : ""
          }`}
          data-action-category="utility"
          data-testid={`${testid}-trigger`}
          aria-label={placeholder}
          disabled={disabled}
        >
          {selected ? (
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">{selected.label}</span>
              {selected.sublabel ? (
                <span className="block truncate text-[11px] font-medium tracking-[0.01em] text-zinc-500">
                  {selected.sublabel}
                </span>
              ) : null}
            </span>
          ) : (
            <span className="truncate font-normal text-zinc-400">
              {placeholder}
            </span>
          )}
          <ChevronDown
            className={`h-3.5 w-3.5 shrink-0 text-zinc-500 transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          side="bottom"
          sideOffset={4}
          collisionPadding={8}
          className="z-50 max-h-[var(--radix-popover-content-available-height)] w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-1rem)] overflow-hidden rounded-lg border bg-popover p-0 text-popover-foreground shadow-brutal outline-none"
          data-testid={`${testid}-menu`}
        >
          <Command className="flex max-h-[var(--radix-popover-content-available-height)] w-full flex-col overflow-hidden rounded-lg bg-popover text-popover-foreground">
            <div className="relative flex items-center border-b">
              <Search className="pointer-events-none absolute left-2.5 h-3.5 w-3.5 text-zinc-400" />
              <Command.Input
                placeholder="Search…"
                className="w-full bg-zinc-50 py-2.5 pl-8 pr-3 text-xs outline-none"
                data-testid={`${testid}-search`}
              />
            </div>
            <Command.List className="min-h-0 max-h-56 overflow-y-auto overflow-x-hidden">
              <Command.Empty className="px-3 py-4 text-center text-xs text-zinc-500">
                No matches.
              </Command.Empty>
              <Command.Group>
                {options.map((option) => (
                  <Command.Item
                    key={option.value}
                    value={`${option.label} ${option.sublabel || ""} ${option.value}`}
                    onSelect={() => select(option.value)}
                    className="cursor-default border-b border-zinc-100 px-3 py-2.5 outline-none last:border-b-0 data-[selected=true]:bg-primary-soft"
                    data-testid={`${testid}-opt-${option.value}`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-xs font-medium text-zinc-900">
                        {option.label}
                      </span>
                      {option.sublabel ? (
                        <span className="mt-0.5 block truncate text-[10px] font-medium tracking-[0.01em] text-zinc-500">
                          {option.sublabel}
                        </span>
                      ) : null}
                    </span>
                  </Command.Item>
                ))}
              </Command.Group>
            </Command.List>
          </Command>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
